import type { AnyID } from '@types'
import { Quaternion, Vector3 } from 'three'
import { createArmRowContext, type ArmPlacementSlot } from './armRowContext'
import {
  ARM_NOTICE_GROUP_FULL,
  ARM_NOTICE_HANDED_SIDE,
  ARM_NOTICE_ONLY_ONE_SIDE,
  allowedArmSideForUnit,
  unitAllowsOnlyOneArm,
} from './singleArmSeriesRules'

export type ArmPlacementEvaluation =
  | { allowed: true; placement: ArmPlacementSlot; appliesSingleArmSeriesRules: true }
  | { allowed: true; appliesSingleArmSeriesRules: false }
  | { allowed: false; message: string }

const getWorldPos = (item: any) => {
  if (item?.matrix) {
    const pos = new Vector3()
    item.matrix.decompose(pos, new Quaternion(), new Vector3())
    return pos
  }
  return new Vector3(Number(item?.x || 0), Number(item?.y || 0), Number(item?.z || 0))
}

const getUnitXAxis = (unit: any) => {
  const q = new Quaternion()
  unit.matrix.decompose(new Vector3(), q, new Vector3())
  return new Vector3(1, 0, 0).applyQuaternion(q)
}

const lipAllowsNewArm = (ctx: ReturnType<typeof createArmRowContext>, lip: ArmPlacementSlot) => {
  const { unit } = lip
  const handedSide = allowedArmSideForUnit(unit?.catalogID)
  if (handedSide != null && lip.side !== handedSide) return false
  if (unitAllowsOnlyOneArm(unit)) {
    return ctx.getArmCountOnUnit(String(unit.id)) === 0
  }
  return true
}

const countArmsOnLip = (
  ctx: ReturnType<typeof createArmRowContext>,
  lip: ArmPlacementSlot,
  getArmSideSign?: (unit: any, arm: any) => 1 | -1,
) => {
  const signFn = getArmSideSign ?? (() => 1 as 1 | -1)
  return ctx
    .getArmsOnUnit(String(lip.unit.id))
    .filter((arm) => signFn(lip.unit, arm) === lip.side).length
}

const pickStackingExteriorPlacement = (
  lips: ArmPlacementSlot[],
  ctx: ReturnType<typeof createArmRowContext>,
  getArmSideSign?: (unit: any, arm: any) => 1 | -1,
): ArmPlacementSlot | null => {
  const stackable = lips.filter((lip) => lipAllowsNewArm(ctx, lip) && !unitAllowsOnlyOneArm(lip.unit))
  if (!stackable.length) return null

  const weighted = stackable.map((lip) => ({
    lip,
    armCount: countArmsOnLip(ctx, lip, getArmSideSign),
  }))
  weighted.sort((a, b) => a.armCount - b.armCount)
  return weighted[0]?.lip ?? null
}

const pickSingleArmSeriesEndPlacement = (
  free: ArmPlacementSlot[],
  ctx: ReturnType<typeof createArmRowContext>,
  referenceUnit: any,
): ArmPlacementSlot | null => {
  const singleArmSeriesFree = free.filter((l) => unitAllowsOnlyOneArm(l.unit))
  if (singleArmSeriesFree.length === 0) return null

  const ref = referenceUnit?.matrix ? referenceUnit : ctx.row[0]
  if (!ref?.matrix) return singleArmSeriesFree[0]

  const refPos = getWorldPos(ref)
  const refX = getUnitXAxis(ref)
  const projected = ctx.row
    .map((unit) => ({
      unit,
      projection: getWorldPos(unit).clone().sub(refPos).dot(refX),
    }))
    .sort((a, b) => a.projection - b.projection)

  const leftEnd = projected[0]?.unit
  const rightEnd = projected[projected.length - 1]?.unit
  if (!leftEnd || !rightEnd) return singleArmSeriesFree[0]

  const armedSingleArmUnits = new Set(
    ctx.row
      .filter((u) => unitAllowsOnlyOneArm(u) && ctx.getArmCountOnUnit(String(u.id)) >= 1)
      .map((u) => String(u.id)),
  )

  const leftLip = singleArmSeriesFree.find(
    (l) => String(l.unit.id) === String(leftEnd.id) && l.side === -1,
  )
  const rightLip = singleArmSeriesFree.find(
    (l) => String(l.unit.id) === String(rightEnd.id) && l.side === 1,
  )

  if (!armedSingleArmUnits.has(String(leftEnd.id)) && leftLip) return leftLip
  if (!armedSingleArmUnits.has(String(rightEnd.id)) && rightLip) return rightLip

  const unarmed = singleArmSeriesFree.filter((l) => !armedSingleArmUnits.has(String(l.unit.id)))
  if (!unarmed.length) return null

  return unarmed[0]
}

const pickPlacement = (
  lips: ArmPlacementSlot[],
  ctx: ReturnType<typeof createArmRowContext>,
  referenceUnit: any,
  getArmSideSign?: (unit: any, arm: any) => 1 | -1,
): ArmPlacementSlot | null => {
  const allowedLips = lips.filter((lip) => lipAllowsNewArm(ctx, lip))

  const refId = String(referenceUnit.id)
  const refArmed = unitAllowsOnlyOneArm(referenceUnit) && ctx.getArmCountOnUnit(refId) >= 1

  if (refArmed) {
    const stackOnMultiArm = pickStackingExteriorPlacement(lips, ctx, getArmSideSign)
    if (stackOnMultiArm) return stackOnMultiArm
  }

  const emptyLips = allowedLips.filter((lip) => !ctx.hasArmOnSide(lip.unit, lip.side))
  if (emptyLips.length) {
    const endPick = pickSingleArmSeriesEndPlacement(emptyLips, ctx, referenceUnit)
    if (endPick) return endPick

    return (
      emptyLips.find((l) => String(l.unit.id) === refId) ||
      emptyLips.find((l) => !unitAllowsOnlyOneArm(l.unit)) ||
      emptyLips[0]
    )
  }

  return pickStackingExteriorPlacement(lips, ctx, getArmSideSign)
}

const resolveBlockMessage = (
  ctx: ReturnType<typeof createArmRowContext>,
  referenceUnit: any,
): string => {
  const singleArmSeriesInRow = ctx.row.filter((u) => unitAllowsOnlyOneArm(u))
  const armedInSeries = singleArmSeriesInRow.filter((u) => ctx.getArmCountOnUnit(String(u.id)) >= 1)

  if (armedInSeries.length > 1) {
    return ARM_NOTICE_GROUP_FULL()
  }
  if (
    allowedArmSideForUnit(referenceUnit?.catalogID) != null &&
    ctx.getArmCountOnUnit(String(referenceUnit.id)) === 0
  ) {
    return ARM_NOTICE_HANDED_SIDE()
  }
  if (unitAllowsOnlyOneArm(referenceUnit) && ctx.getArmCountOnUnit(String(referenceUnit.id)) >= 1) {
    return ARM_NOTICE_ONLY_ONE_SIDE()
  }
  return ARM_NOTICE_ONLY_ONE_SIDE()
}

export function evaluateSingleArmSeriesPlacement(
  items: Map<AnyID, any>,
  referenceUnit: any,
  options?: {
    excludeArmId?: string
    getArmSideSign?: (unit: any, arm: any) => 1 | -1
  },
): ArmPlacementEvaluation {
  const ctx = createArmRowContext(items, referenceUnit, options)

  const hasSingleArmSeriesUnits = ctx.row.some((u) => unitAllowsOnlyOneArm(u))

  if (!hasSingleArmSeriesUnits) {
    return { allowed: true, appliesSingleArmSeriesRules: false }
  }

  const placement = pickPlacement(
    ctx.listExteriorLips(),
    ctx,
    referenceUnit,
    options?.getArmSideSign,
  )
  if (!placement) {
    return { allowed: false, message: resolveBlockMessage(ctx, referenceUnit) }
  }

  return { allowed: true, placement, appliesSingleArmSeriesRules: true }
}

export function hasFreeArmPlacementSlot(
  items: Map<AnyID, any>,
  referenceUnit: any,
  existingArms: any[],
  getArmSideSign: (unit: any, arm: any) => 1 | -1,
): boolean {
  const evaluation = evaluateSingleArmSeriesPlacement(items, referenceUnit, {
    getArmSideSign,
  })
  if (!evaluation.allowed) {
    const ctx = createArmRowContext(items, referenceUnit, { getArmSideSign })
    return pickStackingExteriorPlacement(ctx.listExteriorLips(), ctx, getArmSideSign) != null
  }
  if (!evaluation.appliesSingleArmSeriesRules) return true

  const { unit, side } = evaluation.placement
  if (!unitAllowsOnlyOneArm(unit)) return true

  const ctx = createArmRowContext(items, referenceUnit, { getArmSideSign })
  if (ctx.hasArmOnSide(unit, side)) {
    return pickStackingExteriorPlacement(ctx.listExteriorLips(), ctx, getArmSideSign) != null
  }

  return !existingArms.some((arm) => {
    const parent = items.get(arm.firstParentID as AnyID) as any
    return parent && String(parent.id) === String(unit.id)
  })
}
